import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Download, Search, X, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import DashboardLayout from "../DashboardLayout";
import server from '../../utils/serverConfig';
import apiClient from '../../utils/apiClient';

const CommodityDashboard = () => {
  const [selectedCommodities, setSelectedCommodities] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [positionsData, setPositionsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get current month name
  const getCurrentMonth = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[new Date().getMonth()];
  };

  // Function to fetch data from API
  const fetchPositionsData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`${server.godseye.baseUrl}mcx_positions_data`);
      const data = response.data;
      setPositionsData(data);
      
      // Set default month after data is fetched
      if (data.length > 0) {
        // Get all unique months from data
        const allMonths = [...new Set(data.flatMap(symbolData => 
          symbolData.data.map(monthData => monthData.month)
        ))];
        
        const currentMonth = getCurrentMonth();
        
        // Check if current month exists in data
        if (allMonths.includes(currentMonth)) {
          setSelectedMonths([currentMonth]);
        } else if (allMonths.length > 0) {
          // Set to the last available month (most recent)
          setSelectedMonths([allMonths[allMonths.length - 1]]);
        }
      }
      
      setError(null);
    } catch (err) {
      setError("Failed to fetch positions data");
      console.error("Error fetching positions data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchPositionsData();
  }, []);

  // Flatten data from API format
  const flattenedData = [];
  positionsData.forEach(symbolData => {
    symbolData.data.forEach(monthData => {
      monthData.pnl_data.forEach(entry => {
        flattenedData.push({
          clientId: entry.client_id,
          pnl: entry.pnl,
          commodity: symbolData.symbol,
          month: monthData.month
        });
      });
    });
  });

  // Get unique values
  const allCommodities = [...new Set(flattenedData.map(item => item.commodity))];
  const allMonths = [...new Set(flattenedData.map(item => item.month))];

  // Toggle selection
  const toggleCommodity = (commodity) => {
    setSelectedCommodities(prev => 
      prev.includes(commodity) 
        ? prev.filter(c => c !== commodity)
        : [...prev, commodity]
    );
  };

  const toggleMonth = (month) => {
    setSelectedMonths(prev => 
      prev.includes(month)
        ? prev.filter(m => m !== month)
        : [...prev, month]
    );
  };

  // Filter data
  const filteredData = flattenedData.filter(item => {
    const matchesCommodity = selectedCommodities.length === 0 || selectedCommodities.includes(item.commodity);
    const matchesMonth = selectedMonths.length === 0 || selectedMonths.includes(item.month);
    const matchesSearch = item.clientId.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCommodity && matchesMonth && matchesSearch;
  });

  // Sort data
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key || !sortConfig.direction) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Handle string comparison
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Handle sort
  const handleSort = (key) => {
    let direction = 'asc';
    
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else if (sortConfig.direction === 'desc') {
        direction = null;
      }
    }
    
    setSortConfig({ key: direction ? key : null, direction });
  };

  // Get sort icon
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey || !sortConfig.direction) {
      return <ArrowUpDown size={12} className="text-zinc-600" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp size={12} className="text-blue-400" />
      : <ArrowDown size={12} className="text-blue-400" />;
  };

  // Calculate summaries
  const total = sortedData.reduce((sum, item) => sum + item.pnl, 0);
  const positive = sortedData.filter(item => item.pnl > 0).reduce((sum, item) => sum + item.pnl, 0);
  const negative = sortedData.filter(item => item.pnl < 0).reduce((sum, item) => sum + item.pnl, 0);
  const profitableCount = sortedData.filter(item => item.pnl > 0).length;

  const formatCurrency = (value) => `₹${value.toFixed(2)}`;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex justify-center items-center">
        <div className="text-zinc-400">Loading positions data...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col justify-center items-center">
        <div className="text-red-400 mb-4">{error}</div>
        <button
          onClick={fetchPositionsData}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <DashboardLayout>


    <div className="flex flex-col relative  w-full max-h-screen overflow-hidden">
      <div className="w-full  space-y-3">
        
        {/* Header */}
        <div className="flex items-center justify-between py-4 px-4 ">
          <div className="flex items-center gap-2">

              <h1 className="text-xl font-bold text-white">Commodity Trades Metrics</h1>
           
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={fetchPositionsData}
              className="flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-white text-sm px-3 py-1.5 rounded border border-zinc-700"
            >
              Refresh
            </button>
            {/* <button className="flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-white text-sm px-3 py-1.5 rounded border border-zinc-700">
              <Download size={14} />
              Export
            </button> */}
          </div>
        </div>

        {/* Summary Cards */}
        <div  className='grid grid-cols-2 gap-4 px-3'>
          {/* Filters - Inline Multi-Select */}
        <div className="bg-zinc-900 rounded border border-zinc-800 p-3 space-y-3">
          {/* Search Box - Full Width */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search Client ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 rounded pl-3 pr-9 py-1.5 text-sm text-white placeholder-zinc-400 focus:outline-none focus:border-zinc-600"
            />
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-400" size={14} />
          </div>

          {/* Commodities - Full Width Row */}
          <div>
            <div className="text-xs text-zinc-400 mb-1.5">Commodities</div>
            <div className="flex flex-wrap gap-1.5">
              {allCommodities.map(commodity => (
                <button
                  key={commodity}
                  onClick={() => toggleCommodity(commodity)}
                  className={`text-xs px-2 py-1 rounded transition-colors ${
                    selectedCommodities.includes(commodity)
                      ? 'bg-blue-600 text-white'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  {commodity}
                </button>
              ))}
            </div>
          </div>

          {/* Months - Full Width Row */}
          <div>
            <div className="text-xs text-zinc-400 mb-1.5">Months</div>
            <div className="flex flex-wrap gap-1.5">
              {allMonths.map(month => (
                <button
                  key={month}
                  onClick={() => toggleMonth(month)}
                  className={`text-xs px-2 py-1 rounded transition-colors ${
                    selectedMonths.includes(month)
                      ? 'bg-blue-600 text-white'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  {month}
                </button>
              ))}
            </div>
          </div>

          {/* Active Filters Display */}
          {(selectedCommodities.length > 0 || selectedMonths.length > 0) && (
            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-zinc-800">
              <span className="text-xs text-zinc-400">Active:</span>
              {selectedCommodities.map(commodity => (
                <span key={commodity} className="inline-flex items-center gap-1 bg-blue-600 text-white text-xs px-2 py-0.5 rounded">
                  {commodity}
                  <X size={12} className="cursor-pointer" onClick={() => toggleCommodity(commodity)} />
                </span>
              ))}
              {selectedMonths.map(month => (
                <span key={month} className="inline-flex items-center gap-1 bg-blue-600 text-white text-xs px-2 py-0.5 rounded">
                  {month}
                  <X size={12} className="cursor-pointer" onClick={() => toggleMonth(month)} />
                </span>
              ))}
              {(selectedCommodities.length > 0 || selectedMonths.length > 0) && (
                <button 
                  onClick={() => { setSelectedCommodities([]); setSelectedMonths([]); }}
                  className="text-xs text-zinc-400 hover:text-white ml-2"
                >
                  Clear All
                </button>
              )}
            </div>
          )}
        </div>

       
        <div className="w-full grid grid-cols-2 gap-3 ">
          <div className="bg-zinc-900 rounded border border-zinc-800 p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-zinc-400 text-xs">Total P&L</span>
              {total >= 0 ? <TrendingUp className="text-emerald-400" size={14} /> : <TrendingDown className="text-red-400" size={14} />}
            </div>
            <div className={`text-lg font-bold ${total >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {formatCurrency(total)}
            </div>
          </div>

          <div className="bg-zinc-900 rounded border border-zinc-800 p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-zinc-400 text-xs">Profit</span>
              <TrendingUp className="text-emerald-400" size={14} />
            </div>
            <div className="text-lg font-bold text-emerald-400">{formatCurrency(positive)}</div>
          </div>

          <div className="bg-zinc-900 rounded border border-zinc-800 p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-zinc-400 text-xs">Loss</span>
              <TrendingDown className="text-red-400" size={14} />
            </div>
            <div className="text-lg font-bold text-red-400">{formatCurrency(negative)}</div>
          </div>

          <div className="bg-zinc-900 rounded border border-zinc-800 p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-zinc-400 text-xs">Win Rate</span>
            </div>
            <div className="text-lg font-bold text-blue-400">
              {sortedData.length > 0 ? ((profitableCount / sortedData.length) * 100).toFixed(1) : 0}%
            </div>
          </div>
        </div>
        </div>


        {/* Data Table */}
        <div className="bg-zinc-900 rounded border border-zinc-800 max-h-[calc(100vh-320px)] overflow-y-auto ">
          <div className="">
            <table className="w-full text-sm">
              <thead className='h-[40px] sticky top-0 bg-zinc-900 z-10'>
                <tr className="bg-zinc-800/50 border-b border-zinc-800">
                  <th 
                    className="text-left px-3 py-2 text-zinc-400 font-medium cursor-pointer hover:bg-zinc-800 select-none"
                    onClick={() => handleSort('clientId')}
                  >
                    <div className="flex items-center gap-1">
                      Client ID
                      {getSortIcon('clientId')}
                    </div>
                  </th>
                  <th 
                    className="text-left px-3 py-2 text-zinc-400 font-medium cursor-pointer hover:bg-zinc-800 select-none"
                    onClick={() => handleSort('commodity')}
                  >
                    <div className="flex items-center gap-1">
                      Commodity
                      {getSortIcon('commodity')}
                    </div>
                  </th>
                  <th 
                    className="text-left px-3 py-2 text-zinc-400 font-medium cursor-pointer hover:bg-zinc-800 select-none"
                    onClick={() => handleSort('month')}
                  >
                    <div className="flex items-center gap-1">
                      Month
                      {getSortIcon('month')}
                    </div>
                  </th>
                  <th 
                    className="text-right px-3 py-2 text-zinc-400 font-medium cursor-pointer hover:bg-zinc-800 select-none"
                    onClick={() => handleSort('pnl')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      P&L
                      {getSortIcon('pnl')}
                    </div>
                  </th>
                  <th className="text-center px-3 py-2 text-zinc-400 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.length > 0 ? (
                  sortedData.map((item, index) => (
                    <tr key={index} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                      <td className="px-3 py-2">
                        <span className="font-mono text-white text-xs">{item.clientId}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className="bg-zinc-800 text-zinc-300 px-2 py-0.5 rounded text-xs">
                          {item.commodity}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-zinc-400 text-xs">{item.month}</span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className={`font-semibold ${item.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {formatCurrency(item.pnl)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {item.pnl >= 0 ? (
                          <span className="inline-flex items-center gap-1 bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded text-xs">
                            <TrendingUp size={10} />
                            Profit
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 bg-red-500/10 text-red-400 px-2 py-0.5 rounded text-xs">
                            <TrendingDown size={10} />
                            Loss
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-3 py-8 text-center text-zinc-600 text-sm">
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        
      </div>
    </div>
    </DashboardLayout>
  );
};

export default CommodityDashboard;


























































// import React, { useState, useEffect } from "react";
// import DashboardLayout from "../DashboardLayout";

// // Individual Asset Card Component
// const AssetCard = ({ assetData }) => {
//   const { symbol, data } = assetData;
//   const [selectedMonth, setSelectedMonth] = useState(data[0]?.month || "");

//   // Get current month data
//   const currentMonthData =
//     data.find((d) => d.month === selectedMonth) || data[0];

//   return (
//     <div className="bg-zinc-800 border border-zinc-700 rounded-lg mb-4 overflow-hidden self-start ">
//       {/* Header with Asset name and Month tabs */}
//       <div className="bg-zinc-900 border-b border-zinc-700">
//         <div className="flex items-stretch">
//           {/* Asset Name */}
//           <div className="bg-zinc-700 border-r border-zinc-600 px-4 py-2 flex items-center">
//             <span className="font-semibold text-zinc-200 text-sm">
//               {symbol}
//             </span>
//           </div>

//           {/* Month Tabs */}
//           <div className="grid grid-cols-6 gap-2 w-full  py-1 px-4">
//             {data.map((monthData, index) => (
//               <button
//                 key={index}
//                 onClick={() => setSelectedMonth(monthData.month)}
//                 className={`px-2 py-1 rounded-lg text-sm font-medium transition-colors ${
//                   selectedMonth === monthData.month
//                     ? "bg-emerald-900 text-white"
//                     : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
//                 }`}
//               >
//                 {monthData.month}
//               </button>
//             ))}
//             {/* <button className={`px-2 py-1 rounded-lg text-sm font-medium transition-colors bg-emerald-900 text-white`}>Jan</button>
//             <button className={`px-2 py-1 rounded-lg text-sm font-medium transition-colors bg-emerald-900 text-white`}>Feb</button>
//             <button className={`px-2 py-1 rounded-lg text-sm font-medium transition-colors bg-emerald-900 text-white`}>Mar</button>
//             <button className={`px-2 py-1 rounded-lg text-sm font-medium transition-colors bg-emerald-900 text-white`}>Apr</button>
//             <button className={`px-2 py-1 rounded-lg text-sm font-medium transition-colors bg-emerald-900 text-white`}>May</button>
//             <button className={`px-2 py-1 rounded-lg text-sm font-medium transition-colors bg-emerald-900 text-white`}>Jun</button>
//             <button className={`px-2 py-1 rounded-lg text-sm font-medium transition-colors bg-emerald-900 text-white`}>Jul</button>
//             <button className={`px-2 py-1 rounded-lg text-sm font-medium transition-colors bg-emerald-900 text-white`}>Aug</button>
//             <button className={`px-2 py-1 rounded-lg text-sm font-medium transition-colors bg-emerald-900 text-white`}>Sep</button>
//             <button className={`px-2 py-1 rounded-lg text-sm font-medium transition-colors bg-emerald-900 text-white`}>Oct</button>
//             <button className={`px-2 py-1 rounded-lg text-sm font-medium transition-colors bg-emerald-900 text-white`}>Nov</button>
//             <button className={`px-2 py-1 rounded-lg text-sm font-medium transition-colors bg-emerald-900 text-white`}>Dec</button>
//       */}
//           </div>
//         </div>
//       </div>

//       {/* Table Content */}
//       <div className="bg-zinc-900">
//         {/* Table Headers */}
//         <div className="grid grid-cols-2 bg-zinc-800 border-b border-zinc-700">
//           <div className="px-4 py-2 text-sm font-medium text-zinc-300 border-r border-zinc-600">
//             Client Id
//           </div>
//           <div className="px-4 py-2 text-sm font-medium text-zinc-300 text-center">
//             Pnl
//           </div>
//         </div>

//         {/* Table Rows */}
//         {currentMonthData?.pnl_data?.map((row, index) => (
//           <div
//             key={index}
//             className="grid grid-cols-2 border-t border-zinc-700 hover:bg-zinc-800 transition-colors"
//           >
//             <div className="px-4 py-2 text-sm text-zinc-200 border-r border-zinc-600">
//               {row.client_id}
//             </div>
//             <div
//               className={`px-4 py-2 text-sm text-right font-medium ${
//                 row.pnl >= 0 ? "text-green-400" : "text-red-400"
//               }`}
//             >
//               {row.pnl >= 0 ? "+" : ""}₹{row.pnl.toFixed(2)}
//             </div>
//           </div>
//         ))}

//         {/* No data message */}
//         {!currentMonthData?.pnl_data?.length && (
//           <div className="px-4 py-6 text-center text-zinc-400 text-sm">
//             No data available for {selectedMonth}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// // Main Dashboard Component
// const CommodityTrades = () => {
//   const [positionsData, setPositionsData] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   // Function to fetch data from API
//   const fetchPositionsData = async () => {
//     try {
//       setLoading(true);
//        const response = await fetch("http://localhost:8000/mcx_positions_data");

//       if (!response.ok) {
//         throw new Error(`HTTP error! status: ${response.status}`);
//       }

//       const data = await response.json();
//       setPositionsData(data);
//       setError(null);
//     } catch (err) {
//       setError("Failed to fetch positions data");
//       console.error("Error fetching positions data:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Fetch data on component mount
//   useEffect(() => {
//     fetchPositionsData();
//   }, []);

//   // Refresh function
//   const handleRefresh = () => {
//     fetchPositionsData();
//   };

//   if (loading) {
//     return (
//       <div className="min-h-screen bg-zinc-900 flex justify-center items-center">
//         <div className="text-zinc-400">Loading positions data...</div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="min-h-screen bg-zinc-900 flex flex-col justify-center items-center">
//         <div className="text-red-400 mb-4">{error}</div>
//         <button
//           onClick={handleRefresh}
//           className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 transition-colors"
//         >
//           Retry
//         </button>
//       </div>
//     );
//   }

//   return (
//     <DashboardLayout>
//       <div className="flex flex-col relative  w-full max-h-screen">
//         <div className="flex items-center justify-between space-x-4 ">
//           <div className="flex justify-between bg-[#0F0F0F] h-[60px] px-6 w-full">
//             <p className="flex items-center text-lg font-semibold text-white">
//              Commodity Historical Trades
//             </p>
//           </div>
//         </div>

//         {/* Summary */}
//         {/* {positionsData.length > 0 && (
//           <div className="mt-6 p-3 bg-zinc-800 border border-zinc-700 rounded-lg max-w-4xl">
//             <div className="text-sm text-zinc-400">
//               Total Assets: {positionsData.length} | Last Updated:{" "}
//               {new Date().toLocaleTimeString()}
//             </div>
//           </div>
//         )} */}

//         {/* Asset Cards */}
//         <div className="grid grid-cols-3 gap-4  px-6 py-4">
//           {positionsData.length === 0 ? (
//             <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-8 text-center">
//               <div className="text-zinc-400">No positions data available</div>
//             </div>
//           ) : (
//             positionsData.map((assetData, index) => (
//               <AssetCard key={index} assetData={assetData} />
//             ))
//           )}
//         </div>
//       </div>
//     </DashboardLayout>
//   );
// };

// export default CommodityTrades;

